/**
 * Section fallback registry (SNT-5).
 *
 * Centralised lookup: `SECTION_FALLBACKS[reportType][fieldPath]` returns
 * a pure deterministic fallback that produces a data-grounded 1-3 sentence
 * string for the given report type and field.
 *
 * The fallback is called when the SNT-1 instruction-shape guard flags
 * the model's output for that field (or when the model returned empty).
 *
 * Each entry must:
 *   - be a pure function (no I/O, no Date.now / Math.random),
 *   - return a non-empty string,
 *   - NEVER produce text that itself trips the instruction-shape regex
 *     — the recursion guard R6 is pinned by the test
 *     `instructionShapeGuard.test.ts -> "fallback recursion guard"`.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ReportTypeId } from '../../config/reportTypes';
import { buildCoverageOnlySentiment, publicSentimentScreeningReadiness } from '../sentiment/publicSentimentScreening';
import { probePublicSentimentEvidence } from '../publicSentimentEvidenceProbe';
import {
  climateImplicationsFallback,
  developmentRiskReasonFallback,
  electricGridFallback,
  executiveSummaryFallback,
  recommendationDescriptionFallback,
  sitingNotesFallback,
  transportFiberFallback,
} from './feasibility';
import {
  environmentalExecutiveSummaryFallback,
  nepaScreeningRationaleFallback,
  esaSpeciesAssessmentFallback,
  envWaterRightsAssessmentFallback,
  femaFloodAnalysisFallback,
  tribalConsultationFallback,
  landUseCompatibilityFallback,
  environmentalVerdictFallback,
  // RTU-2 (row 0.99235) — three NEPA-canonical composers.
  alternativesAnalysisFallback,
  cumulativeImpactsFallback,
  publicParticipationPlanFallback,
} from './environmental';
import {
  powerExecutiveSummaryFallback,
  substationAssessmentFallback,
  interconnectionQueueFallback,
  capacityAnalysisFallback,
  curtailmentRiskFallback,
  rateStructureFallback,
  renewableIntegrationFallback,
  redundancyAssessmentFallback,
  powerRecommendationFallback,
  powerStrategyRequirementsFallback,
  powerStrategyPostureFallback,
  powerStrategyScopeFallback,
} from './powerAvailability';
import {
  waterExecutiveSummaryFallback,
  supplyAvailabilityFallback,
  groundwaterAssessmentFallback,
  surfaceWaterAssessmentFallback,
  waterRightsAnalysisFallback,
  droughtResilienceFallback,
  coolingWaterRequirementsFallback,
  wastewaterManagementFallback,
  waterRecommendationFallback,
} from './waterResource';
import {
  testFitExecutiveSummaryFallback,
  siteConstraintsFallback,
  buildingPlacementFallback,
  setbackComplianceFallback,
  stormwaterDetentionFallback,
  utilityRoutingFallback,
  phaseLayoutFallback,
  testFitRecommendationFallback,
  // RTU-3 (row 0.99235) — two build-deliverability composers.
  constructabilityAssessmentFallback,
  costRangeEstimateFallback,
} from './testFit';
import {
  interconnectionExecutiveSummaryFallback,
  fiberAvailabilityFallback,
  carrierDiversityFallback,
  ixProximityFallback,
  submarineCableFallback,
  latencyModelingFallback,
  networkResilienceFallback,
  edgeDeploymentFallback,
  interconnectionRecommendationFallback,
} from './interconnection';
import {
  DERIVED_SECTION_FALLBACKS,
  type ReportDerivedFallbackFn,
} from './derivedFallbacks';

export type SectionFallbackFn = (site: SiteEntity, sed: SiteEvidenceDocument) => string;

/* ── Public sentiment: the barren-scan branch ──────────────────────────────
 *
 * WORD CHOICE HERE IS LOAD-BEARING, in two directions.
 *
 * 1. `evaluateSocial` (src/lib/portfolio/pillarScorer.ts) matches
 *    /opposition|NIMBY|hostile|litigation/i and is NEGATION-BLIND. Prose
 *    reading "no organised opposition was identified" therefore scores the
 *    social pillar as though opposition were PRESENT (risk 4.5) and lowers the
 *    site's composite — a report saying it found nothing would make the site
 *    look worse than one that was never run. None of those tokens appear
 *    below, and none may be added without fixing the evaluator first.
 * 2. The mirror-image failure is prose that reads as endorsement. "strong
 *    support", "favorable sentiment" and "positive community" are matched by
 *    the same evaluator in the opposite direction, and are equally absent.
 *
 * The text also has to clear `DEFAULT_EXEC_MIN` (600 chars) in
 * `SECTION_DEPTH_REQUIREMENTS['public-sentiment'].executive`, and must not
 * trip `detectInstructionShape` — so it is declarative throughout, with no
 * "this section should…" constructions.
 */
function barrenExecutive(attempted: number): string {
  return `The saved scan records ${attempted} source attempts but contains no usable facts or observations for this assessment. The legacy receipt does not establish complete source coverage. Missing or failed retrieval is not evidence that public records or community concerns do not exist.\n\n`
    + 'This is an ABSENCE OF EVIDENCE, not a finding about how this community regards the project. It must not be read as acceptance, endorsement, or an absence of concern. Community stance and confidence remain unknown, and no support index can be assigned from these empty results.\n\n'
    + 'The remaining sections are omitted because their findings cannot be supported. Refresh collection with dated, location-bound source receipts and sources with genuine local coverage. Refreshing failed sources may add evidence; regenerating prose alone does not resolve the evidence gap. No exhaustive search or final community-support conclusion is established.';
}

const NEVER_SEARCHED_EXECUTIVE =
  'No public-sentiment scan has been run for this site, so nothing is known either way about local '
  + 'views on the project. This is not a finding and must not be read as one: no sources have been '
  + 'searched, no records reviewed, and no community position established.\n\n'
  + 'This differs from a scan that ran and found nothing, which would be a statement about source '
  + 'coverage. Here the assessment simply has not been performed yet, and running it is what '
  + 'produces an answer. Until it runs, every section below is unevidenced, and any stance '
  + 'attributed to this community would be invention rather than analysis.';

function neverSearchedDetail(label: string): string {
  return (
    `The ${label} is unevidenced because no public-sentiment scan has been run for this site. `
    + 'Running the scan is what populates it; nothing here has been searched or reviewed.'
  );
}

/**
 * Resolve a public-sentiment section fallback against the actual state of the
 * evidence, rather than assuming a re-run will help.
 *
 * `detailLabel === null` marks the executive section, which is the ONE section
 * that must still carry text when the scan came back barren — it is where the
 * gap is stated. Every other section returns `''`, which
 * `applySectionFallbacks` leaves untouched, so the section stays empty and
 * `computeSectionOmissions` can record it. Filling them is what made the
 * omission machinery unreachable in production.
 */
function publicSentimentFallback(
  sed: SiteEvidenceDocument,
  detailLabel: string | null,
  generatorFailureText: string,
): string {
  const probe = probePublicSentimentEvidence(sed);
  const readiness = publicSentimentScreeningReadiness(sed);
  if (readiness.ready && readiness.receipt.outcome === 'coverage-only') return detailLabel === null ? buildCoverageOnlySentiment(readiness.receipt).executive : '';
  // Legacy empty scans retain honest uncertainty without inventing coverage.
  if (probe.empty) return detailLabel === null ? barrenExecutive(probe.attempted) : '';
  // Never ran — a run genuinely IS the fix, and saying so is honest.
  if (probe.neverSearched) {
    return detailLabel === null ? NEVER_SEARCHED_EXECUTIVE : neverSearchedDetail(detailLabel);
  }
  // Evidence exists but the generator left the field empty: a real generator
  // failure, where the original "pending re-run" wording is accurate.
  return generatorFailureText;
}

/** A6 — report-scoped fallbacks (render-time); also exposed on SECTION_FALLBACKS for tests. */
export type { ReportDerivedFallbackFn };
export { DERIVED_SECTION_FALLBACKS, deriveRiskMatrix } from './derivedFallbacks';

/** Field paths filled at render-time from report data — skipped by applySectionFallbacks. */
export const REPORT_DERIVED_FALLBACK_PATHS = new Set<string>(['riskMatrix']);

/**
 * SNT-5 replaced the previous `pendingRerunFallback(label)` placeholder
 * helper with concrete data-grounded composers per report type. The
 * helper was dropped because every non-feasibility entry now reads
 * from the SED directly (see ./environmental, ./powerAvailability,
 * ./waterResource, ./testFit, ./interconnection).
 */

export const SECTION_FALLBACKS: Record<ReportTypeId, Record<string, SectionFallbackFn>> = {
  // ─── Feasibility — primary blast-radius surface ─────────────────────
  feasibility: {
    executiveSummary: executiveSummaryFallback,
    sitingNotes: sitingNotesFallback,
    'climateSevereWeather.implications': climateImplicationsFallback,
    'infrastructureInterconnection.electricGrid': electricGridFallback,
    'infrastructureInterconnection.transportFiber': transportFiberFallback,
    'developmentRisks[].reason': developmentRiskReasonFallback,
    'recommendations[].description': recommendationDescriptionFallback,
    // ── STAGE 5.1: why the absorbed grid sections get NO composer here ──
    //
    // Feasibility now carries the six grid-screening sections, and it would be
    // one line each to point them at the `powerAnalysis.*` composers below.
    // That is deliberately NOT done, because those composers never return
    // empty — `substationAssessmentFallback` answers 'Substation assessment
    // pending re-run.' when `sed.grid.data` is absent, and its siblings behave
    // the same. They are written that way ON PURPOSE: those sections are
    // REQUIRED for power-availability, so a non-empty answer is what keeps the
    // export gate satisfiable.
    //
    // For feasibility the same sections are `evidenceOptional`, and an
    // always-on composer would convert every legitimate omission into prose
    // that reads as pending analysis. Two things would break: the report
    // regains the hollow-section mode (a section that reads as analysis while
    // asserting nothing), and `sourceFloor` stops seeing the omission it caps
    // on, because a section with fallback text is not omitted.
    //
    // So an ungrounded grid section on a feasibility report stays absent and
    // records its omission. If a future decision wants grounded text here, the
    // composers must first gain an empty return for the no-data case.
    //
    // Phase 3 part B (2026-09-07): the same rule for the WATER SCREEN sections
    // absorbed from the retired water report (`supplyAvailability`,
    // `groundwater`, `waterRights`, `droughtResilience`) — the water composers
    // above answer 'pending re-run' when the SED is silent, which is exactly
    // the hollow-section mode. They stay `evidenceOptional` with no composer.
  },

  // ─── Environmental ─────────────────────────────────────────────────
  // SNT-5: data-grounded composers reading sed.environmental,
  // sed.flood, sed.parcel, sed.airQuality, sed.waterStress.
  environmental: {
    executiveSummary: environmentalExecutiveSummaryFallback,
    'nepaScreening.rationale': nepaScreeningRationaleFallback,
    'ecologicalAssessment.esaSpeciesAssessment': esaSpeciesAssessmentFallback,
    'waterResources.waterRightsAssessment': envWaterRightsAssessmentFallback,
    'floodNaturalHazards.femaFloodAnalysis': femaFloodAnalysisFallback,
    'culturalHistorical.tribalConsultation': tribalConsultationFallback,
    'landUseZoning.compatibilityAnalysis': landUseCompatibilityFallback,
    'conclusion.environmentalVerdict': environmentalVerdictFallback,
    // RTU-2 (row 0.99235) — three NEPA-canonical composers. The
    // schema fields are bare STRING (not nested objects), so the
    // fieldPath is the top-level field name.
    alternativesAnalysis: alternativesAnalysisFallback,
    cumulativeImpacts: cumulativeImpactsFallback,
    publicParticipationPlan: publicParticipationPlanFallback,
  },

  // ─── Power Availability ────────────────────────────────────────────
  // SNT-5: data-grounded composers reading sed.grid, sed.electricity,
  // sed.solar, sed.wind.
  'power-availability': {
    executiveSummary: powerExecutiveSummaryFallback,
    'powerStrategyContext.requirementsSummary': powerStrategyRequirementsFallback,
    'powerStrategyContext.strategyPosture': powerStrategyPostureFallback,
    'powerStrategyContext.analysisScope': powerStrategyScopeFallback,
    'powerAnalysis.substationAssessment': substationAssessmentFallback,
    'powerAnalysis.interconnectionQueue': interconnectionQueueFallback,
    'powerAnalysis.capacityAnalysis': capacityAnalysisFallback,
    'powerAnalysis.curtailmentRisk': curtailmentRiskFallback,
    'powerAnalysis.rateStructure': rateStructureFallback,
    'powerAnalysis.renewableIntegration': renewableIntegrationFallback,
    'powerAnalysis.redundancyAssessment': redundancyAssessmentFallback,
    'conclusion.recommendation': powerRecommendationFallback,
  },

  // ─── Water Resource ────────────────────────────────────────────────
  // SNT-5: data-grounded composers reading sed.waterStress, sed.climate,
  // sed.flood, sed.onSiteInfrastructure.waterFeatures.
  'water-resource': {
    executiveSummary: waterExecutiveSummaryFallback,
    'waterAnalysis.supplyAvailability': supplyAvailabilityFallback,
    'waterAnalysis.groundwaterAssessment': groundwaterAssessmentFallback,
    'waterAnalysis.surfaceWaterAssessment': surfaceWaterAssessmentFallback,
    'waterAnalysis.waterRightsAnalysis': waterRightsAnalysisFallback,
    'waterAnalysis.droughtResilience': droughtResilienceFallback,
    'waterAnalysis.coolingWaterRequirements': coolingWaterRequirementsFallback,
    'waterAnalysis.wastewaterManagement': wastewaterManagementFallback,
    'conclusion.recommendation': waterRecommendationFallback,
  },

  // ─── Test Fit ──────────────────────────────────────────────────────
  // SNT-5: data-grounded composers reading sed.siteProfile, sed.parcel,
  // sed.flood, sed.climate, sed.onSiteInfrastructure.
  'test-fit': {
    executiveSummary: testFitExecutiveSummaryFallback,
    'testFitAnalysis.siteConstraints': siteConstraintsFallback,
    'testFitAnalysis.buildingPlacement': buildingPlacementFallback,
    'testFitAnalysis.setbackCompliance': setbackComplianceFallback,
    'testFitAnalysis.stormwaterDetention': stormwaterDetentionFallback,
    'testFitAnalysis.utilityRouting': utilityRoutingFallback,
    'testFitAnalysis.phaseLayout': phaseLayoutFallback,
    'conclusion.recommendation': testFitRecommendationFallback,
    // RTU-3 (row 0.99235) — two build-deliverability composers. The
    // schema fields are bare STRING (not nested objects), so the
    // fieldPath is the top-level field name.
    constructabilityAssessment: constructabilityAssessmentFallback,
    costRangeEstimate: costRangeEstimateFallback,
    riskMatrix: DERIVED_SECTION_FALLBACKS['test-fit']!.riskMatrix as unknown as SectionFallbackFn,
  },

  // ─── Interconnection ───────────────────────────────────────────────
  // SNT-5: data-grounded composers reading sed.connectivity,
  // sed.onSiteInfrastructure corridors.
  interconnection: {
    executiveSummary: interconnectionExecutiveSummaryFallback,
    'interconnectionAnalysis.fiberAvailability': fiberAvailabilityFallback,
    'interconnectionAnalysis.carrierDiversity': carrierDiversityFallback,
    'interconnectionAnalysis.ixProximity': ixProximityFallback,
    'interconnectionAnalysis.submarineCable': submarineCableFallback,
    'interconnectionAnalysis.latencyModeling': latencyModelingFallback,
    'interconnectionAnalysis.networkResilience': networkResilienceFallback,
    'interconnectionAnalysis.edgeDeployment': edgeDeploymentFallback,
    'conclusion.recommendation': interconnectionRecommendationFallback,
  },

  // ─── Public Sentiment — PSR-1 (row 0.99290) ────────────────────────
  // PSR-1 inline placeholder fallbacks, one per PSR section. SNT-5
  // dropped the shared `pendingRerunFallback(label)` helper because
  // the other 6 report types' fallbacks read directly from the SED;
  // the PSR sections can't do that until PSE-1 publishes the
  // `publicSentiment` SED section + PSR-2 wires data-grounded
  // composers (stakeholder map / theme polarity / trend slope). Until
  // then each section returns a deterministic 1-2 sentence "pending
  // re-run" string. The string is hard-coded (no SED read, no random)
  // so the R6 fallback-recursion guard stays green and the
  // instruction-shape regex never matches the body.
  // Each composer branches on whether the scan RAN AND FOUND NOTHING. The two
  // states license opposite sentences, and conflating them is what shipped the
  // Redhead report: eight sections each promising that a re-run "will publish"
  // stakeholder positions, polarity decompositions and evidence counts, on a
  // site where every attempted source had returned nothing. Those are not
  // placeholders — they are assertions about future evidence that does not
  // exist, and they read as body prose.
  //
  // Barren scan  ⇒ the seven detail sections return '' so they stay empty and
  //                `computeSectionOmissions` can record them (a POPULATED
  //                section is never omitted, so a placeholder here silently
  //                disables the omission machinery downstream); `executive`
  //                carries the deterministic statement of the gap.
  // Never ran    ⇒ the scan genuinely can be run, so say that.
  // Ran, has data ⇒ a real generator failure; "pending re-run" is honest and
  //                the original wording is kept verbatim.
  'public-sentiment': {
    executive:               (_s, sed) => publicSentimentFallback(sed, null,
      'Public-sentiment executive roll-up pending re-run; PSE-1 will publish stakeholder + theme drivers.'),
    stakeholderMap:          (_s, sed) => publicSentimentFallback(sed, 'stakeholder map',
      'Stakeholder map pending re-run; PSE-1 will surface elected, regulatory, neighbourhood, tribal, and NGO positions.'),
    perBtmSentiment:         (_s, sed) => publicSentimentFallback(sed, 'per-technology sentiment breakdown',
      'Per-BTM sentiment pending re-run; PSE-1 will decompose polarity across SMR, gas, solar+BESS, wind+BESS, and hybrid technologies.'),
    themeDeepDives:          (_s, sed) => publicSentimentFallback(sed, 'theme deep-dives',
      'Theme deep-dives pending re-run; PSE-1 will surface dominant themes (water, noise, tax base, grid impact, environmental justice) with cited evidence counts.'),
    trendAnalysis:           (_s, sed) => publicSentimentFallback(sed, 'trend analysis',
      'Trend analysis pending re-run; PSE-1 will compute the 90-day polarity slope and mention-volume direction over the user-specified trend window.'),
    comparableSiteBenchmark: (_s, sed) => publicSentimentFallback(sed, 'comparable-site benchmark',
      'Comparable-site benchmark pending re-run; PSE-1 will cross-reference the RAG-corpus similar-site precedent injection for analogous theme + outcome pairings.'),
    mitigationPlaybook:      (_s, sed) => publicSentimentFallback(sed, 'mitigation playbook',
      'Mitigation playbook pending re-run; PSE-1 will sequence engagement actions tied to specific theme + stakeholder pairs from comparable-site outcomes.'),
    appendix:                (_s, sed) => publicSentimentFallback(sed, 'evidence appendix',
      'Public-sentiment appendix pending re-run; PSE-1 will inventory the supporting evidence by provider (GDELT / MuniCode / Regulations.gov / news article) with [Ref N] citations.'),
  },

  'custom-brief': {
    executiveSummary: () => 'Custom brief executive summary pending re-run.',
    projectContext: () => 'Custom brief project context pending re-run.',
    customBriefQuestions: () => 'Custom brief Q&A pending re-run.',
  },

  'executive-summary': {
    executiveSummary: () => 'Executive synthesis pending re-run — select source reports and regenerate.',
    btmViability: () => 'BTM viability assessment pending re-run.',
    crossReportSynthesis: () => 'Cross-report domain synthesis pending re-run.',
    criticalRisks: () => 'Critical risks synthesis pending re-run.',
    sourceReports: () => 'Source report index pending re-run.',
  },

  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. The body is computed
  // LIVE from the deterministic engine + the report.financialAnalysis stamp;
  // there are no LLM-narrative sections to fall back on, so the map is empty.
  financial: {},

  'supply-chain-risk': {
    procurementOverview: () => 'Procurement overview pending re-run — BoM and supplier posture will populate from the supply chain plan.',
    criticalPathProcurement: () => 'Critical path assessment pending re-run.',
    supplyChainRisks: () => 'Supply chain risk synthesis pending re-run.',
    supplierRiskRanking: () => 'Supplier risk ranking pending re-run.',
    quoteComparisons: () => 'Quote comparison insights pending re-run.',
    supplyChainRiskMatrix: () => 'Supply chain risk matrix pending re-run.',
    actionPlan: () => 'Mitigation action plan pending re-run.',
  },
};

/**
 * Resolve a fallback by (reportType, fieldPath). Returns `undefined`
 * when no registered fallback exists — callers should fall back to a
 * generic "pending re-run" string in that case.
 */
export function getSectionFallback(
  reportType: ReportTypeId,
  fieldPath: string,
): SectionFallbackFn | undefined {
  return SECTION_FALLBACKS[reportType]?.[fieldPath];
}
